import { useEffect, useState } from "react";
import { Mail, Clock, ShieldCheck, CheckCircle2, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { SERVICES, CONTACT_EMAIL } from "../data/content";
import { Reveal, SectionHeading } from "./Reveal";
import { API, formatApiErrorDetail } from "../lib/api";

export default function EnquiryForm({ prefillService, estimateMsg }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [service, setService] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (prefillService) setService(prefillService);
  }, [prefillService]);

  useEffect(() => {
    if (estimateMsg) setMessage(estimateMsg);
  }, [estimateMsg]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSending(true);
    try {
      const res = await fetch(`${API}/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name,
          email,
          service: service || "Something else / not sure",
          message,
          estimate: estimateMsg || undefined,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(formatApiErrorDetail(data.detail));
      setSent(true);
      toast.success("Enquiry sent — thank you!");
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors focus:border-cyan-400/60 focus:bg-white/[0.06]";

  return (
    <section id="enquiry" className="relative overflow-hidden border-t border-white/5 bg-[#0A1128] py-20 md:py-28" data-testid="enquiry-section">
      <div className="pointer-events-none absolute -left-40 top-0 h-96 w-96 rounded-full bg-[radial-gradient(closest-side,rgba(0,240,255,0.08),transparent)]" />
      <div className="mx-auto grid max-w-7xl gap-12 px-5 sm:px-8 lg:grid-cols-[0.85fr_1.15fr]">
        <div>
          <SectionHeading
            overline="Get in touch"
            title="Tell me what's not working — or what could work better"
            copy="Describe your problem in plain English. You will get a personal reply with an honest assessment and a clear, no-obligation quote — usually within one working day."
          />
          <Reveal delay={0.15}>
            <div className="mt-8 space-y-4">
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                data-testid="contact-email-link"
                className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5 transition-colors hover:border-cyan-500/30"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan-500/10 text-cyan-400">
                  <Mail size={18} />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Email directly</span>
                  <span className="block text-sm font-medium text-slate-100">{CONTACT_EMAIL}</span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600/15 text-blue-400">
                  <Clock size={18} />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Availability</span>
                  <span className="block text-sm font-medium text-slate-100">Evenings & weekends · remote or on-site</span>
                </span>
              </div>
              <div className="flex items-center gap-4 rounded-2xl border border-white/10 bg-white/[0.03] p-5">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-400">
                  <ShieldCheck size={18} />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Your data</span>
                  <span className="block text-sm font-medium text-slate-100">
                    Handled confidentially, never shared, GDPR-respecting
                  </span>
                </span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.1}>
          <div className="rounded-2xl border border-cyan-500/20 bg-[#0D152D] p-6 shadow-[0_0_50px_rgba(0,102,255,0.12)] md:p-8" data-testid="enquiry-form-card">
            {sent ? (
              <div className="flex h-full min-h-[380px] flex-col items-center justify-center text-center" data-testid="enquiry-success">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15">
                  <CheckCircle2 size={32} className="text-emerald-400" />
                </span>
                <h3 className="font-display mt-6 text-xl font-bold text-slate-50">Enquiry sent</h3>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-slate-400">
                  Thank you — your message has been received. You will get a personal reply, usually within one working day.
                </p>
                <button
                  data-testid="enquiry-another-button"
                  onClick={() => {
                    setSent(false);
                    setName("");
                    setEmail("");
                    setMessage("");
                  }}
                  className="mt-6 rounded-full border border-white/15 px-6 py-2.5 text-sm font-medium text-slate-200 transition-colors hover:border-cyan-400/50 hover:text-cyan-300"
                >
                  Send another enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5" data-testid="enquiry-form">
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="enquiry-name" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                      Your name
                    </label>
                    <input
                      id="enquiry-name"
                      data-testid="enquiry-name-input"
                      className={inputCls}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="e.g. Alex Smith"
                      required
                      minLength={2}
                    />
                  </div>
                  <div>
                    <label htmlFor="enquiry-email" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                      Email address
                    </label>
                    <input
                      id="enquiry-email"
                      data-testid="enquiry-email-input"
                      type="email"
                      className={inputCls}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="enquiry-service" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    Service needed
                  </label>
                  <select
                    id="enquiry-service"
                    data-testid="enquiry-service-select"
                    className={`${inputCls} appearance-none`}
                    value={service}
                    onChange={(e) => setService(e.target.value)}
                    required
                  >
                    <option value="" disabled className="bg-[#0D152D]">
                      Choose a service…
                    </option>
                    {SERVICES.map((s) => (
                      <option key={s.id} value={s.title} className="bg-[#0D152D]">
                        {s.title} ({s.rate})
                      </option>
                    ))}
                    <option value="Something else / not sure" className="bg-[#0D152D]">
                      Something else / not sure
                    </option>
                  </select>
                </div>
                <div>
                  <label htmlFor="enquiry-message" className="mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    How can I help?
                  </label>
                  <textarea
                    id="enquiry-message"
                    data-testid="enquiry-message-input"
                    className={`${inputCls} min-h-[130px] resize-y`}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Describe the problem or the outcome you want — plain English is perfect."
                    required
                    minLength={10}
                  />
                </div>
                {error && (
                  <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300" data-testid="enquiry-error">
                    {error}
                  </p>
                )}
                <button
                  type="submit"
                  data-testid="enquiry-submit-button"
                  disabled={sending}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-cyan-400 to-blue-600 px-7 py-3.5 text-sm font-semibold text-[#04101F] shadow-[0_0_28px_rgba(0,240,255,0.35)] transition-transform duration-200 hover:scale-[1.02] disabled:opacity-60 sm:w-auto"
                >
                  {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                  {sending ? "Sending…" : "Send enquiry"}
                </button>
                <p className="text-[11px] leading-relaxed text-slate-500">
                  Your details are used only to respond to your enquiry and are never shared with third parties.
                </p>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
